import dayjs from "dayjs";
import { formatFairPayDate } from "@components/utils/fairPayFormat";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";

export const SUBSCRIPTION_CARD_STATE = {
  LAPSED: "lapsed",
  CURRENT_CLOSED: "current-closed",
  CURRENT_OPEN: "current-open",
  FUTURE_CLOSED: "future-closed",
  FUTURE_OPEN: "future-open",
};

const OPEN_STATES = new Set([
  SUBSCRIPTION_CARD_STATE.CURRENT_OPEN,
  SUBSCRIPTION_CARD_STATE.FUTURE_OPEN,
]);

export function toDateKey(value) {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

export function formatCardAmount(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return value;
    value = numeric;
  }
  return formatPayRangeAmount(value).replace(/\.0(?=[KMB]|$)/, "");
}

/**
 * Derive visual state by comparing the subscription window to today.
 * There is no `state` prop — the card computes this internally.
 */
export function deriveSubscriptionState(
  subscriptionEffectiveFrom,
  subscriptionEffectiveTo,
  today = dayjs()
) {
  const from = toDateKey(subscriptionEffectiveFrom);
  const to = toDateKey(subscriptionEffectiveTo);
  const now = toDateKey(today) || dayjs().format("YYYY-MM-DD");

  if (to && now > to) return SUBSCRIPTION_CARD_STATE.LAPSED;
  if (from && now >= from) {
    return to
      ? SUBSCRIPTION_CARD_STATE.CURRENT_CLOSED
      : SUBSCRIPTION_CARD_STATE.CURRENT_OPEN;
  }
  if (from && now < from) {
    return to
      ? SUBSCRIPTION_CARD_STATE.FUTURE_CLOSED
      : SUBSCRIPTION_CARD_STATE.FUTURE_OPEN;
  }
  return SUBSCRIPTION_CARD_STATE.LAPSED;
}

export function canUnsubscribe(state) {
  return OPEN_STATES.has(state);
}

/** Computed State (Lapsed / Current / Future) for a card state; null when it cannot be placed. */
export function subscriptionPeriodState(state) {
  switch (state) {
    case SUBSCRIPTION_CARD_STATE.LAPSED:
      return "lapsed";
    case SUBSCRIPTION_CARD_STATE.CURRENT_OPEN:
    case SUBSCRIPTION_CARD_STATE.CURRENT_CLOSED:
      return "current";
    case SUBSCRIPTION_CARD_STATE.FUTURE_OPEN:
    case SUBSCRIPTION_CARD_STATE.FUTURE_CLOSED:
      return "future";
    default:
      return null;
  }
}

export function subscriptionSubtitle(state, from, to) {
  const fromLabel = formatFairPayDate(from);
  const toLabel = formatFairPayDate(to);

  if (OPEN_STATES.has(state)) {
    return fromLabel ? `Effective from ${fromLabel}` : "";
  }
  return fromLabel && toLabel ? `Effective from ${fromLabel} to ${toLabel}` : "";
}

/**
 * Version window line never shows a date past the subscription's own end.
 * Start side is never capped.
 */
export function displayedVersionTo(subscriptionEffectiveTo, versionEffectiveTo) {
  const subTo = toDateKey(subscriptionEffectiveTo);
  const verTo = toDateKey(versionEffectiveTo);
  if (!subTo) return versionEffectiveTo ?? null;
  if (!verTo) return subscriptionEffectiveTo;
  return verTo < subTo ? versionEffectiveTo : subscriptionEffectiveTo;
}

export function versionWindowLine(
  versionEffectiveFrom,
  versionEffectiveTo,
  subscriptionEffectiveTo,
  versionNumber
) {
  const fromLabel = formatFairPayDate(versionEffectiveFrom);
  const toValue = displayedVersionTo(subscriptionEffectiveTo, versionEffectiveTo);
  const toLabel = formatFairPayDate(toValue);
  const versionLabel = versionNumber != null && versionNumber !== "" ? `Version ${versionNumber}` : "";

  if (!fromLabel) return versionLabel;
  if (!toLabel) {
    return versionLabel
      ? `Effective from ${fromLabel} · ${versionLabel}`
      : `Effective from ${fromLabel}`;
  }
  return versionLabel
    ? `${fromLabel} - ${toLabel} · ${versionLabel}`
    : `${fromLabel} - ${toLabel}`;
}

/**
 * Rule 5 — Previous-version chevron target date.
 * Clamped to no earlier than the subscription's own effective_from, so the
 * as-of date never leaves the subscription's window even when the adjacent
 * pay_range_version's dates extend earlier. Null when no previous version
 * exists on this subscription (the chevron is hidden).
 */
export function versionPreviousTargetDate(previousVersionEffectiveTo, subscriptionEffectiveFrom) {
  if (previousVersionEffectiveTo == null || previousVersionEffectiveTo === "") return null;
  const prevTo = toDateKey(previousVersionEffectiveTo);
  if (!prevTo) return null;
  const subFrom = toDateKey(subscriptionEffectiveFrom);
  if (subFrom && subFrom > prevTo) return subFrom;
  return prevTo;
}

/**
 * Rule 5 — Next-version chevron target date.
 * Clamped to no later than the subscription's own effective_to (when set), so
 * the as-of date never leaves the subscription's window even when the adjacent
 * pay_range_version's dates extend later. Null when no next version exists on
 * this subscription (the chevron is hidden).
 */
export function versionNextTargetDate(nextVersionEffectiveFrom, subscriptionEffectiveTo) {
  if (nextVersionEffectiveFrom == null || nextVersionEffectiveFrom === "") return null;
  const nextFrom = toDateKey(nextVersionEffectiveFrom);
  if (!nextFrom) return null;
  const subTo = toDateKey(subscriptionEffectiveTo);
  if (subTo && subTo < nextFrom) return subTo;
  return nextFrom;
}
